/**
 * Dashboard.tsx — strona po zalogowaniu super-admina (F2 MVP + F8 polish 2026-04-26).
 *
 * F2 MVP: 4 counter cards (tenants/modules/subscriptions/admins) z hardcoded info card.
 * F8 polish (2026-04-26): wymiana hardcoded info card na "Ostatnie 5 zdarzeń"
 * z `recentAudit`, hinty pod kartami pokazują per-status breakdown z nowego
 * kontraktu `/api/_dev/counters`. i18n migracja hardcode'ów do `admin.dashboard.*`.
 *
 * Data: GET `/api/dev/counters` — endpoint deprecated (do migracji w T8.8 close).
 *
 * F8 dorzuci: pełen panel super-admin (lista tenantów ✅ T7.1, impersonate T8.5b,
 * suspend T8.4a, faktury PDF T8.7).
 * Drag-and-drop dashboard builder — Etap 2.
 */

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Badge } from '@/components/Badge';
import { Card } from '@/components/Card';
import { Layout } from '@/components/Layout';
import { Skeleton } from '@/components/Skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { humanizeAuditAction } from '@/lib/humanize-audit-action';

interface StatusBreakdown {
  trialing: number;
  active: number;
  past_due: number;
  cancelled: number;
  suspended: number;
}

interface AuditEntry {
  id: string;
  action: string;
  adminEmail: string | null;
  tenantCode: string | null;
  createdAt: string;
}

interface DashboardCounters {
  tenants: number;
  tenantsByStatus: StatusBreakdown;
  modules: number;
  subscriptions: number;
  subscriptionsByStatus: StatusBreakdown;
  admins: number;
  recentAudit: AuditEntry[];
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('pl-PL', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function Dashboard() {
  const { t } = useTranslation();
  const { admin } = useAuth();

  const countersQuery = useQuery<DashboardCounters>({
    queryKey: ['admin', 'dashboard', 'counters'],
    queryFn: async () => {
      const res = await api.get<DashboardCounters>('/dev/counters');
      return res.data;
    },
    retry: false,
  });

  const data = countersQuery.data;

  return (
    <Layout title={t('admin.dashboard.title')}>
      <section className="mx-auto max-w-7xl px-6 py-8">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-text-primary">
            {t('admin.dashboard.greeting', { name: admin?.name ?? admin?.email ?? '' })}
          </h2>
          <p className="mt-1 text-sm text-text-tertiary">{t('admin.dashboard.subtitle')}</p>
        </div>

        {countersQuery.isLoading ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i}>
                <Skeleton height={16} width="60%" />
                <Skeleton height={36} width="40%" className="mt-3" />
                <Skeleton height={12} width="80%" className="mt-2" />
              </Card>
            ))}
          </div>
        ) : countersQuery.isError ? (
          <Card className="border-red-300 bg-red-50 dark:bg-red-900/20">
            <p className="text-sm text-red-700 dark:text-red-300" role="alert">
              {t('admin.dashboard.loadError')}
            </p>
          </Card>
        ) : data ? (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <CounterCard
                label={t('admin.dashboard.cards.tenants')}
                value={data.tenants}
                hint={t('admin.dashboard.hints.tenantsBreakdown', {
                  active: data.tenantsByStatus.active,
                  trialing: data.tenantsByStatus.trialing,
                  past_due: data.tenantsByStatus.past_due,
                })}
                testId="dashboard-card-tenants"
              />
              <CounterCard
                label={t('admin.dashboard.cards.modules')}
                value={data.modules}
                hint={t('admin.dashboard.hints.modulesCatalog', { count: data.modules })}
                testId="dashboard-card-modules"
              />
              <CounterCard
                label={t('admin.dashboard.cards.subscriptions')}
                value={data.subscriptions}
                hint={t('admin.dashboard.hints.subscriptionsBreakdown', {
                  active: data.subscriptionsByStatus.active,
                  trialing: data.subscriptionsByStatus.trialing,
                  past_due: data.subscriptionsByStatus.past_due,
                })}
                testId="dashboard-card-subscriptions"
              />
              <CounterCard
                label={t('admin.dashboard.cards.admins')}
                value={data.admins}
                hint={t('admin.dashboard.hints.adminsCount')}
                testId="dashboard-card-admins"
              />
            </div>

            <Card className="mt-8" padding="md">
              <h3 className="mb-3 text-sm font-semibold text-text-primary">
                {t('admin.dashboard.audit.heading')}
              </h3>
              {data.recentAudit.length === 0 ? (
                <p className="text-sm text-text-muted">{t('admin.dashboard.audit.empty')}</p>
              ) : (
                <ul className="flex flex-col gap-2" data-testid="dashboard-audit-list">
                  {data.recentAudit.map((entry) => (
                    <li
                      key={entry.id}
                      className="flex flex-wrap items-center gap-3 rounded-lg border border-border-secondary px-3 py-2 text-sm"
                    >
                      <span className="font-mono text-xs text-text-muted">
                        {formatDateTime(entry.createdAt)}
                      </span>
                      <Badge color="gray" title={entry.action}>
                        {humanizeAuditAction(entry.action, t).label}
                      </Badge>
                      <span className="text-xs text-text-tertiary">
                        {entry.adminEmail ?? t('admin.dashboard.audit.noAdmin')}
                      </span>
                      {entry.tenantCode && (
                        <span className="font-mono text-xs text-text-tertiary">
                          → {entry.tenantCode}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Card>
          </>
        ) : null}
      </section>
    </Layout>
  );
}

interface CounterCardProps {
  label: string;
  value: number;
  hint: string;
  testId?: string;
}

function CounterCard({ label, value, hint, testId }: CounterCardProps) {
  return (
    <Card>
      <div
        className="text-xs font-medium uppercase tracking-wide text-text-muted"
        data-testid={testId}
      >
        {label}
      </div>
      <div className="mt-2 text-3xl font-semibold text-text-primary">{value}</div>
      <div className="mt-1 text-xs text-text-muted">{hint}</div>
    </Card>
  );
}
